import styled from 'styled-components'
import { Link } from 'react-router-dom'

import {
  HUB_SIDEBAR_MOBILE_BREAKPOINT,
  HUB_SIDEBAR_WIDTH_COLLAPSED,
  HUB_SIDEBAR_WIDTH_OPEN,
} from '../../../constants/breakpoints'

const TOP_BAR_HEIGHT = '80px'
const MENU_ITEM_HEIGHT = '51px'
const SIDEBAR_WIDTH_OPEN = HUB_SIDEBAR_WIDTH_OPEN
const SIDEBAR_WIDTH_COLLAPSED = HUB_SIDEBAR_WIDTH_COLLAPSED
const SIDEBAR_DRAWER_TRANSITION = 'width 0.2s ease'

export const SidebarWrapper = styled.aside<{ $isOpen: boolean; $fullHeight?: boolean; $collapsedWidth?: string }>`
  position: fixed;
  top: ${({ $fullHeight }) => ($fullHeight ? '0' : TOP_BAR_HEIGHT)};
  left: 0;
  height: ${({ $fullHeight }) => ($fullHeight ? '100vh' : `calc(100vh - ${TOP_BAR_HEIGHT})`)};
  width: ${({ $isOpen, $collapsedWidth }) =>
    $isOpen ? SIDEBAR_WIDTH_OPEN : $collapsedWidth ?? SIDEBAR_WIDTH_COLLAPSED};
  background: ${({ theme }) => theme.colors.sidebarBackground};
  box-shadow: -3px 0px 15px 5px ${({ theme }) => theme.colors.indigoShadow};
  transition: ${SIDEBAR_DRAWER_TRANSITION};
  display: flex;
  flex-direction: column;
  z-index: 100;
  overflow-x: hidden;

  /* Mobile: keep icon rail when collapsed (same width rule as desktop).
   * fullHeight (logo in sidebar): rail covers the viewport including the top bar.
   * Keep z-index above sticky page headers so lateral header bleeds cannot hide the rail. */
  @media screen and (max-width: ${HUB_SIDEBAR_MOBILE_BREAKPOINT}) {
    ${({ $fullHeight }) =>
      $fullHeight
        ? `
      top: 0;
      height: 100vh;
    `
        : ''}
    max-width: 85vw;
    z-index: 100;
  }
`

export const TopLogoWrapper = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${TOP_BAR_HEIGHT};
  flex-shrink: 0;
  overflow: hidden;
  width: 100%;
  background: transparent;
  border: none;
  cursor: pointer;
`

export const TopLogoImg = styled.img<{
  $isOpen: boolean
  $expandedWidth?: string
  $expandedHeight?: string
  $collapsedWidth?: string
  $collapsedHeight?: string
}>`
  width: ${({ $isOpen, $expandedWidth, $collapsedWidth }) =>
    $isOpen ? $expandedWidth || '125.152px' : $collapsedWidth || '42px'};
  height: ${({ $isOpen, $expandedHeight, $collapsedHeight }) =>
    $isOpen ? $expandedHeight || '56px' : $collapsedHeight || '42px'};
  object-fit: contain;
  transition: width 0.2s ease, height 0.2s ease;
`

export const ToggleButton = styled.button<{ $isOpen: boolean; $collapsedWidth?: string }>`
  position: fixed;
  top: calc(${TOP_BAR_HEIGHT} + 10px);
  left: ${({ $isOpen, $collapsedWidth }) =>
    $isOpen
      ? `calc(${SIDEBAR_WIDTH_OPEN} - 12px)`
      : `calc(${$collapsedWidth ?? SIDEBAR_WIDTH_COLLAPSED} - 12px)`};
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.grey800};
  box-shadow: 0px 0px 5px 2px ${({ theme }) => theme.colors.grey800};
  border: none;
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  z-index: 101;
  transition: left 0.2s ease, background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0px 0px 2px 2px ${({ theme }) => theme.colors.primary};
  }
`

/**
 * The scrollable menu region. Exists because MenuList used to sit in a plain <nav>, which broke the
 * height chain out of SidebarWrapper: a bare <nav> is a flex ITEM but not a flex CONTAINER, so
 * MenuList's `flex: 1` was inert, and its `max-height: <pct>` resolved against an indefinite height
 * (CSS 2.1 §10.7 → treated as `none`). `overflow-y: auto` therefore never engaged and a menu taller
 * than the sidebar simply overflowed past the bottom edge with no scrollbar — the surplus items were
 * unreachable. `min-height: 0` is the load-bearing declaration: without it a flex item refuses to
 * shrink below its content height and overflow still never engages.
 */
export const MenuNav = styled.nav`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`

// Clears the absolutely-positioned SidebarFooter so the last item can always be scrolled out from
// under it. Inside the scroll container, so it costs nothing visually when the menu is short.
const FOOTER_CLEARANCE = '60px'

export const MenuList = styled.ul<{ $topGap?: string; $maxHeight?: string }>`
  list-style: none;
  margin: 0;
  padding: ${({ $topGap }) => $topGap ?? '32px'} 0 ${FOOTER_CLEARANCE};
  flex: 1 1 auto;
  min-height: 0;
  ${({ $maxHeight }) => ($maxHeight ? `max-height: ${$maxHeight};` : '')}
  overflow-y: auto;
  overflow-x: hidden;
`

export const MenuItemWrapper = styled.li<{ $active?: boolean; $gap?: string }>`
  display: flex;
  flex-direction: column;
  margin-top: ${({ $gap }) => $gap ?? '0px'};
  background-color: ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};

  &:first-child {
    margin-top: 0;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
  }
`

export const MenuItemLink = styled(Link)<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isOpen }) => ($isOpen ? 'flex-start' : 'center')};
  gap: ${({ $isOpen }) => ($isOpen ? '14px' : '0')};
  padding: ${({ $isOpen }) => ($isOpen ? '8px 8px 8px 15px' : '8px 0')};
  min-height: ${MENU_ITEM_HEIGHT};
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fonts.body}, sans-serif;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.white};
    text-decoration: none;
  }
`

export const MenuItemButton = styled.button<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isOpen }) => ($isOpen ? 'flex-start' : 'center')};
  gap: ${({ $isOpen }) => ($isOpen ? '14px' : '0')};
  padding: ${({ $isOpen }) => ($isOpen ? '15px 8px 15px 15px' : '15px 0')};
  min-height: ${MENU_ITEM_HEIGHT};
  width: 100%;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  text-align: left;
  box-sizing: border-box;
  font-family: ${({ theme }) => theme.fonts.body}, sans-serif;

  &:hover {
    color: ${({ theme }) => theme.colors.white};
  }
`

export const MenuItemLabel = styled.span<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? 'inline' : 'none')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s ease;
  pointer-events: none;
  overflow: hidden;
  white-space: nowrap;
`

export const MenuItemIconWrap = styled.span<{ $iconSize?: string }>`
  width: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $iconSize }) => $iconSize ?? '24px'};
`

export const SubMenuList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0 0 0 36px;
`

export const SubMenuItemLink = styled(Link)`
  display: block;
  padding: 0.5rem 1rem;
  color: rgba(255, 255, 255, 0.75);
  text-decoration: none;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus {
    color: rgba(255, 255, 255, 0.75);
  }

  &:hover {
    color: ${({ theme }) => theme.colors.white};
    text-decoration: none;
  }
`

export const SidebarFooter = styled.a<{ $bottom?: string }>`
  display: flex;
  justify-content: flex-end;
  position: absolute;
  right: 10px;
  bottom: ${({ $bottom }) => $bottom ?? '10px'};
  cursor: pointer;
`

export const FooterLogo = styled.img<{ $isOpen: boolean }>`
  width: ${({ $isOpen }) => ($isOpen ? '50px' : '30px')};
  object-fit: contain;
  transition: width 0.2s ease;
`
